import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./clients.css";
import { PCB_CORE_URL } from "../../config";
import { useStore } from "../../store";

const API_BASE = (PCB_CORE_URL || "").replace(/\/+$/, "");
const API_CLIENTS = `${API_BASE}/superuser/clients/`;
const API_CLIENT_DETAIL = (id) => `${API_BASE}/superuser/clients/${id}/`;
const API_SET_RANK = (id) => `${API_BASE}/superuser/clients/${id}/set-rank/`;

function buildBusinessDraft(client) {
  return {
    company_name: client?.company_name || client?.effective_company_name || "",
    company_identifier: client?.company_identifier || "",
    phone_number: client?.phone_number || "",
    country: client?.country || "",
    billing_address_line1: client?.billing_address_line1 || "",
    billing_address_line2: client?.billing_address_line2 || "",
    billing_postal_code: client?.billing_postal_code || "",
    billing_city: client?.billing_city || "",
    billing_country: client?.billing_country || client?.country || "",
    shipping_same_as_billing: client?.shipping_same_as_billing ?? true,
    shipping_contact_name: client?.shipping_contact_name || "",
    shipping_service: client?.shipping_service || "",
    shipping_address_line1: client?.shipping_address_line1 || "",
    shipping_address_line2: client?.shipping_address_line2 || "",
    shipping_postal_code: client?.shipping_postal_code || "",
    shipping_city: client?.shipping_city || "",
    shipping_country: client?.shipping_country || client?.country || "",
    vat_mode: client?.vat_mode || (Number(client?.effective_vat_rate_percent || 0) <= 0 ? "exempt" : "standard"),
    vat_rate_percent: client?.vat_rate_percent || client?.effective_vat_rate_percent || "20.00",
    company_discount_kind: client?.company_discount_kind || (client?.effective_discount_kind === "none" ? "" : client?.effective_discount_kind || ""),
    company_discount_value: client?.company_discount_value || client?.effective_discount_value || "",
  };
}

function formatVat(client) {
  const rate = Number(client?.effective_vat_rate_percent || client?.vat_rate_percent || 0);
  return rate <= 0 ? "Exonéré" : `${rate}%`;
}

function formatDiscount(client) {
  const kind = String(client?.effective_discount_kind || client?.company_discount_kind || "none").toLowerCase();
  const rawValue = client?.effective_discount_value ?? client?.company_discount_value;
  const numericValue = Number(rawValue);
  if ((!rawValue && rawValue !== 0) || kind === "none" || kind === "") return "-";

  const formattedValue = Number.isFinite(numericValue)
    ? numericValue.toLocaleString("fr-FR", { maximumFractionDigits: 2 })
    : String(rawValue);

  return kind === "pct" ? `-${formattedValue}%` : `-${formattedValue} EUR`;
}

export default function ClientsPage() {
  const access = useStore((s) => s.user.access);

  const apiFetch = useCallback(
    async (url, options = {}) => {
      return fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
          ...(options.headers || {}),
        },
      });
    },
    [access]
  );

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [q, setQ] = useState("");
  const [onlyMasters, setOnlyMasters] = useState(false);

  const masters = useMemo(() => {
    return (items || []).filter((c) => (c.rank || "").toLowerCase() === "master");
  }, [items]);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        const params = new URLSearchParams();
        if (q.trim()) params.set("q", q.trim());
        if (onlyMasters) params.set("only_masters", "true");

        const url = `${API_CLIENTS}?${params.toString()}`;
        const res = await apiFetch(url, { method: "GET" });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${t}`.trim());
        }

        const data = await res.json();
        const arr = Array.isArray(data) ? data : data?.results || [];
        if (alive) setItems(arr);
      } catch (e) {
        if (alive) setErr(e?.message || "Erreur lors du chargement.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [apiFetch, q, onlyMasters]);

  async function setRank(clientId, rank) {
    setSavingId(clientId);
    setErr("");
    try {
      const res = await apiFetch(API_SET_RANK(clientId), {
        method: "POST",
        body: JSON.stringify({ rank }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${t}`.trim());
      }
      const updated = await res.json();
      setItems((prev) => (prev || []).map((x) => (x.id === updated.id ? { ...x, rank: updated.rank } : x)));
    } catch (e) {
      setErr(e?.message || "Erreur");
    } finally {
      setSavingId(null);
    }
  }

  function startEdit(client) {
    setEditingId(client.id);
    setDrafts((prev) => ({ ...prev, [client.id]: buildBusinessDraft(client) }));
  }

  function updateDraft(clientId, key, value) {
    setDrafts((prev) => ({
      ...prev,
      [clientId]: {
        ...(prev[clientId] || {}),
        [key]: value,
      },
    }));
  }

  async function saveBusiness(clientId) {
    const payload = drafts[clientId];
    if (!payload) return;

    setSavingId(clientId);
    setErr("");
    try {
      const res = await apiFetch(API_CLIENT_DETAIL(clientId), {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${t}`.trim());
      }
      const updated = await res.json();
      setItems((prev) => (prev || []).map((x) => (x.id === updated.id ? updated : x)));
      setEditingId(null);
    } catch (e) {
      setErr(e?.message || "Erreur.");
    } finally {
      setSavingId(null);
    }
  }

  return (
    <div className="boClients">
      <div className="boClientsHeader">
        <div>
          <h1 className="boClientsTitle">Clients</h1>
        </div>

        <div className="boClientsFilters">
          <input
            className="boInput"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Recherche (email, nom,SIRET...)"
          />

          {/* <label className="boRow boClientsChk">
            <input type="checkbox" checked={onlyMasters} onChange={(e) => setOnlyMasters(e.target.checked)} />
            Uniquement masters
          </label> */}

          <button className="boBtn" onClick={() => window.location.reload()}>
            Rafraîchir
          </button>
        </div>
      </div>

      {err ? (
        <div className="boCard boError">
          <div className="boErrorTitle">Erreur</div>
          <div className="boErrorText">{err}</div>
        </div>
      ) : null}

      {loading ? (
        <div className="boCard">
          <div className="boSkeletonRow" />
          <div className="boSkeletonRow" />
          <div className="boSkeletonRow" />
        </div>
      ) : items.length === 0 ? (
        <div className="boCard boEmpty">
          <div className="boEmptyTitle">Aucun client</div>
          <div className="boEmptyText">Vérifie que l'endpoint <code>/superuser/clients/</code> renvoie des données.</div>
        </div>
      ) : (
        <div className="boCard">
          <div className="boTableWrap">
            <table className="boTable">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Email</th>
                  <th>Société</th>
                  <th>Nom</th>
                  <th>TVA</th>
                  <th>Remise</th>
                  {/* <th>Statut</th>
                  <th>Master</th> */}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const isAdmin = !!c.is_staff || !!c.is_superuser;
                  const isEditing = editingId === c.id;
                  const draft = drafts[c.id] || buildBusinessDraft(c);

                  return (
                    <React.Fragment key={c.id}>
                      <tr>
                        <td className="mono">#{c.id}</td>
                        <td className="mono">{c.email}</td>
                        <td>{c.effective_company_name || c.company_name || "-"}</td>
                        <td>{`${c.first_name || ""} ${c.last_name || ""}`.trim() || "-"}</td>
                        <td>{formatVat(c)}</td>
                        <td>{formatDiscount(c)}</td>
                        <td>
                          <button className="boBtn" onClick={() => (isEditing ? setEditingId(null) : startEdit(c))}>
                            {isEditing ? "Fermer" : "Editer"}
                          </button>
                        </td>
                      </tr>
                      {isEditing ? (
                        <tr>
                          <td colSpan={7}>
                            <div className="boFormGrid" style={{ padding: 12 }}>
                              <label className="boLabel">
                                Société
                                <input className="boInput" value={draft.company_name} onChange={(e) => updateDraft(c.id, "company_name", e.target.value)} />
                              </label>
                              <label className="boLabel">
                                SIRET / identifiant
                                <input className="boInput mono" value={draft.company_identifier} onChange={(e) => updateDraft(c.id, "company_identifier", e.target.value)} />
                              </label>
                              <label className="boLabel">
                                TVA
                                <select className="boInput" value={draft.vat_mode} onChange={(e) => updateDraft(c.id, "vat_mode", e.target.value)}>
                                  <option value="standard">20%</option>
                                  <option value="exempt">Exonéré</option>
                                  <option value="custom">Personnalisé</option>
                                </select>
                              </label>
                              <label className="boLabel">
                                Taux TVA (%)
                                <input className="boInput mono" value={draft.vat_rate_percent} onChange={(e) => updateDraft(c.id, "vat_rate_percent", e.target.value)} disabled={draft.vat_mode !== "custom"} />
                              </label>
                              <label className="boLabel">
                                Remise entreprise
                                <select className="boInput" value={draft.company_discount_kind} onChange={(e) => updateDraft(c.id, "company_discount_kind", e.target.value)}>
                                  <option value="">Aucune</option>
                                  <option value="pct">%</option>
                                  <option value="amount">Montant</option>
                                </select>
                              </label>
                              <label className="boLabel">
                                Valeur remise
                                <input className="boInput mono" value={draft.company_discount_value ?? ""} onChange={(e) => updateDraft(c.id, "company_discount_value", e.target.value)} />
                              </label>
                              <label className="boLabel">
                                Téléphone
                                <input className="boInput" value={draft.phone_number} onChange={(e) => updateDraft(c.id, "phone_number", e.target.value)} />
                              </label>
                              <label className="boLabel">
                                Pays
                                <input className="boInput" value={draft.country} onChange={(e) => updateDraft(c.id, "country", e.target.value)} />
                              </label>
                              <label className="boLabel">
                                Adresse facturation 1
                                <input className="boInput" value={draft.billing_address_line1} onChange={(e) => updateDraft(c.id, "billing_address_line1", e.target.value)} />
                              </label>
                              <label className="boLabel">
                                Adresse facturation 2
                                <input className="boInput" value={draft.billing_address_line2} onChange={(e) => updateDraft(c.id, "billing_address_line2", e.target.value)} />
                              </label>
                              <label className="boLabel">
                                Code postal facturation
                                <input className="boInput" value={draft.billing_postal_code} onChange={(e) => updateDraft(c.id, "billing_postal_code", e.target.value)} />
                              </label>
                              <label className="boLabel">
                                Ville facturation
                                <input className="boInput" value={draft.billing_city} onChange={(e) => updateDraft(c.id, "billing_city", e.target.value)} />
                              </label>
                              <label className="boLabel">
                                Pays facturation
                                <input className="boInput" value={draft.billing_country} onChange={(e) => updateDraft(c.id, "billing_country", e.target.value)} />
                              </label>
                              <label className="boLabel boRow" style={{ alignItems: "center", gap: 8 }}>
                                <input type="checkbox" checked={!!draft.shipping_same_as_billing} onChange={(e) => updateDraft(c.id, "shipping_same_as_billing", e.target.checked)} />
                                Utiliser la même adresse pour la livraison
                              </label>
                              {/* <label className="boLabel">
                                Nom livraison
                                <input className="boInput" value={draft.shipping_contact_name} onChange={(e) => updateDraft(c.id, "shipping_contact_name", e.target.value)} disabled={draft.shipping_same_as_billing} />
                              </label>
                              <label className="boLabel">
                                Service livraison
                                <input className="boInput" value={draft.shipping_service} onChange={(e) => updateDraft(c.id, "shipping_service", e.target.value)} disabled={draft.shipping_same_as_billing} />
                              </label> */}
                              <label className="boLabel">
                                Adresse livraison 1
                                <input className="boInput" value={draft.shipping_address_line1} onChange={(e) => updateDraft(c.id, "shipping_address_line1", e.target.value)} disabled={draft.shipping_same_as_billing} />
                              </label>
                              <label className="boLabel">
                                Adresse livraison 2
                                <input className="boInput" value={draft.shipping_address_line2} onChange={(e) => updateDraft(c.id, "shipping_address_line2", e.target.value)} disabled={draft.shipping_same_as_billing} />
                              </label>
                              <label className="boLabel">
                                Code postal livraison
                                <input className="boInput" value={draft.shipping_postal_code} onChange={(e) => updateDraft(c.id, "shipping_postal_code", e.target.value)} disabled={draft.shipping_same_as_billing} />
                              </label>
                              <label className="boLabel">
                                Ville livraison
                                <input className="boInput" value={draft.shipping_city} onChange={(e) => updateDraft(c.id, "shipping_city", e.target.value)} disabled={draft.shipping_same_as_billing} />
                              </label>
                              <label className="boLabel">
                                Pays livraison
                                <input className="boInput" value={draft.shipping_country} onChange={(e) => updateDraft(c.id, "shipping_country", e.target.value)} disabled={draft.shipping_same_as_billing} />
                              </label>
                            </div>
                            <div style={{ display: "flex", gap: 8, padding: "0 12px 12px" }}>
                              <button className="boBtn boBtnPrimary" onClick={() => saveBusiness(c.id)} disabled={savingId === c.id}>
                                {savingId === c.id ? "Enregistrement..." : "Sauver les réglages entreprise"}
                              </button>
                              {/* {c.master_account_id ? <div className="boHint">Ce profil hérite de l'entreprise du master #{c.master_account_id}. Les modifications s'appliqueront à tout le groupe.</div> : null} */}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="boTableFooter">
            {/* <div className="boHint">Les réglages TVA, remise et adresses sont partagés par entreprise. Les sous-comptes héritent automatiquement du compte master.</div> */}
          </div>
        </div>
      )}
    </div>
  );
}


